/**
 * Hook for offline-first supply request creation and sync.
 *
 * - Online: normal API calls + caches resource types for future offline use
 * - Offline: creates requests locally in IndexedDB
 * - Auto-syncs on reconnect
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { offlineSupplyRequestService, SyncResult } from '../offline/supply-request.service';
import { networkService } from '@/services/network.service';
import { api } from '@/services/api';
import type { OfflineSupplyRequest, CachedResourceType } from '../offline/db';

interface UseOfflineSupplyRequestsReturn {
  /** All offline supply requests (from the offlineSupplyRequests table) */
  requests: OfflineSupplyRequest[];
  /** Cached resource types for dropdown population */
  resourceTypes: CachedResourceType[];
  /** Whether the device is currently offline */
  isOffline: boolean;
  /** Number of unsynced offline supply requests */
  unsyncedCount: number;
  /** Whether a sync is currently in progress */
  isSyncing: boolean;
  /** Manually trigger sync of all pending requests */
  syncNow: () => Promise<SyncResult>;
  /** Create a new supply request (works offline) */
  createRequest: (data: Omit<OfflineSupplyRequest, 'localId' | 'is_synced' | 'status' | 'created_at' | 'updated_at'>) => Promise<OfflineSupplyRequest>;
  /** Update an existing offline supply request */
  updateRequest: (localId: number, updates: Partial<OfflineSupplyRequest>) => Promise<OfflineSupplyRequest | null>;
  /** Refresh data from IndexedDB */
  refresh: () => Promise<void>;
}

export function useOfflineSupplyRequests(incidentId?: string): UseOfflineSupplyRequestsReturn {
  const [requests, setRequests] = useState<OfflineSupplyRequest[]>([]);
  const [resourceTypes, setResourceTypes] = useState<CachedResourceType[]>([]);
  const [isOffline, setIsOffline] = useState(!networkService.isOnline());
  const [unsyncedCount, setUnsyncedCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncInProgressRef = useRef(false);

  // Refresh offline requests and unsynced count from IndexedDB
  const refresh = useCallback(async () => {
    try {
      const [allRequests, count] = await Promise.all([
        offlineSupplyRequestService.getAll(incidentId),
        offlineSupplyRequestService.getUnsyncedCount(),
      ]);
      setRequests(allRequests);
      setUnsyncedCount(count);
    } catch (err) {
      console.warn('[useOfflineSupplyRequests] Failed to refresh:', err);
    }
  }, [incidentId]);

  // Load cached resource types from IndexedDB
  const loadCachedResourceTypes = useCallback(async () => {
    try {
      const cached = await offlineSupplyRequestService.getCachedResourceTypes();
      if (cached.length > 0) {
        setResourceTypes(cached);
      }
    } catch {
      // Ignore — will populate from server when online
    }
  }, []);

  // Fetch resource types from server and cache them
  const fetchAndCacheResourceTypes = useCallback(async () => {
    if (!networkService.isOnline()) return;

    try {
      // Supply-request resource types live under /supply-requests/resource-types
      // (a bare `/resource-types` route does not exist on the backend). The
      // endpoint returns a bare array, already filtered to is_active = true.
      const data = await api.get<
        Array<{
          id: string;
          category_id: string;
          code: string;
          name: string;
          description?: string;
          unit_of_measure?: string;
          category?: { id: string; code: string; name: string };
        }>
      >('/supply-requests/resource-types');

      const types: CachedResourceType[] = (data || []).map(rt => ({
        id: rt.id,
        category_id: rt.category_id,
        code: rt.code,
        name: rt.name,
        description: rt.description,
        unit_of_measure: rt.unit_of_measure,
        category_name: rt.category?.name,
        category_code: rt.category?.code,
      }));

      await offlineSupplyRequestService.cacheResourceTypes(types);
      setResourceTypes(types);
    } catch {
      // Server fetch failed — use whatever is cached
      console.warn('[useOfflineSupplyRequests] Failed to fetch resource types from server');
    }
  }, []);

  // Sync all pending offline requests
  const syncNow = useCallback(async (): Promise<SyncResult> => {
    if (syncInProgressRef.current) {
      return { success: false, synced: 0, failed: 0, errors: ['Sync already in progress'] };
    }

    syncInProgressRef.current = true;
    setIsSyncing(true);

    try {
      const result = await offlineSupplyRequestService.syncOfflineRequests();
      await refresh();
      return result;
    } finally {
      syncInProgressRef.current = false;
      setIsSyncing(false);
    }
  }, [refresh]);

  // Create a new offline supply request
  const createRequest = useCallback(async (
    data: Omit<OfflineSupplyRequest, 'localId' | 'is_synced' | 'status' | 'created_at' | 'updated_at'>
  ): Promise<OfflineSupplyRequest> => {
    const created = await offlineSupplyRequestService.create(data);
    await refresh();

    // If online, try immediate sync
    if (networkService.isOnline()) {
      syncNow().catch(() => {});
    }

    return created;
  }, [refresh, syncNow]);

  // Update an existing offline supply request
  const updateRequest = useCallback(async (
    localId: number,
    updates: Partial<OfflineSupplyRequest>
  ): Promise<OfflineSupplyRequest | null> => {
    const updated = await offlineSupplyRequestService.update(localId, updates);
    await refresh();
    return updated;
  }, [refresh]);

  // Initial load: fetch offline data and resource types
  useEffect(() => {
    refresh();
    loadCachedResourceTypes();
    fetchAndCacheResourceTypes();
  }, [refresh, loadCachedResourceTypes, fetchAndCacheResourceTypes]);

  // Listen for online/offline status changes
  useEffect(() => {
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const removeListener = networkService.addListener((connected) => {
      setIsOffline(!connected);

      if (connected) {
        // Auto-sync on reconnect after a short debounce
        if (reconnectTimer) clearTimeout(reconnectTimer);
        reconnectTimer = setTimeout(() => {
          reconnectTimer = null;
          syncNow().catch(() => {});
          fetchAndCacheResourceTypes();
        }, 2000);
      } else {
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
      }
    });

    return () => {
      removeListener();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [syncNow, fetchAndCacheResourceTypes]);

  // Poll unsynced count every 15 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const count = await offlineSupplyRequestService.getUnsyncedCount();
        setUnsyncedCount(count);
      } catch {
        // Ignore polling errors
      }
    }, 15_000);

    return () => clearInterval(interval);
  }, []);

  return {
    requests,
    resourceTypes,
    isOffline,
    unsyncedCount,
    isSyncing,
    syncNow,
    createRequest,
    updateRequest,
    refresh,
  };
}
